import {
  ClampToEdgeWrapping,
  Data3DTexture,
  LinearFilter,
  RGBAFormat,
  UnsignedByteType,
} from 'three/webgpu';

/**
 * A procedural colour-grading LUT (no licensed asset needed): gentle contrast, cool shadows,
 * warm highlights, a touch of saturation. Applied after tone mapping on display-referred colour.
 */
export function createGradeLut(size = 32): Data3DTexture {
  const data = new Uint8Array(size * size * size * 4);
  const n = size - 1;
  let i = 0;
  for (let b = 0; b < size; b++) {
    for (let g = 0; g < size; g++) {
      for (let r = 0; r < size; r++) {
        let rr = r / n;
        let gg = g / n;
        let bb = b / n;
        const lum = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
        // Saturation 1.06 around luminance.
        rr = lum + (rr - lum) * 1.06;
        gg = lum + (gg - lum) * 1.06;
        bb = lum + (bb - lum) * 1.06;
        // Contrast 1.05 around mid-grey.
        rr = (rr - 0.5) * 1.05 + 0.5;
        gg = (gg - 0.5) * 1.05 + 0.5;
        bb = (bb - 0.5) * 1.05 + 0.5;
        // Split toning: cool shadows, warm highlights.
        const shadow = (1 - lum) * (1 - lum);
        const highlight = lum * lum;
        rr += -0.012 * shadow + 0.03 * highlight;
        gg += -0.004 * shadow + 0.012 * highlight;
        bb += 0.03 * shadow - 0.015 * highlight;
        data[i++] = Math.round(Math.min(1, Math.max(0, rr)) * 255);
        data[i++] = Math.round(Math.min(1, Math.max(0, gg)) * 255);
        data[i++] = Math.round(Math.min(1, Math.max(0, bb)) * 255);
        data[i++] = 255;
      }
    }
  }
  const texture = new Data3DTexture(data, size, size, size);
  texture.format = RGBAFormat;
  texture.type = UnsignedByteType;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.wrapR = ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}
